<template>
  <div class="hcr-wrap">
    <div class="hcr-screen" v-if="!loading || reportData">

      <div class="hcr-center" v-if="loading">
        <div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>
      </div>

      <div class="hcr-center" v-else-if="error">
        <p>{{ error }}</p>
      </div>

      <!-- ============ SCREEN 1 : OVERVIEW ============ -->
      <template v-else-if="reportData && reportData.summary && currentView === 'main'">
        <div class="header1">
          <div class="avatar-wrap">
            <img src="@/assets/images/elder-avatar.png" alt="avatar" class="avatar-img"/>
          </div>
          <div class="name-block">
            <div class="name-pill">{{ reportData.resident_name || '--' }}</div>
          </div>
        </div>

        <div class="hcr-picker">
          <VueCtkDateTimePicker
            v-model="selectedDate"
            :only-date="true"
            format="YYYY-MM-DD"
            formatted="M月D日"
            :max-date="maxDate"
            :min-date="null"
            :no-header="true"
            :no-button="false"
            :auto-close="true"
            locale="zh-TW"
            color="#388E3C"
            label="選擇日期"
            @input="onDateChange"
          />
        </div>

        <!-- Sleep card -->
        <div class="gcard">
          <div class="pill-row">
            <div class="pill cyan" @click="currentView = 'weekly'">一週趨勢分析</div>
            <div class="pill cyan" @click="currentView = 'hourly'">一日分時分析</div>
            <svg class="hand" style="left:28px; top:32px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 12V5.5a1.5 1.5 0 0 1 3 0V11" stroke="#222" stroke-width="1.4" fill="#fff"/>
              <path d="M12 11V4.5a1.5 1.5 0 0 1 3 0V11" stroke="#222" stroke-width="1.4" fill="#fff"/>
              <path d="M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
            <svg class="hand" style="left:185px; top:32px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
            <div class="know-pill" @click="currentView = 'sleepTips'">睡眠<br/>小知識</div>
            <svg class="hand" style="right:6px; top:74px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>

          <div class="time-range">{{ sleepWindow }}</div>

          <div class="stat-row">
            <div class="label">夜間休息時長：</div>
            <div>
              <span :class="['value-lg', sleepHourColor]">{{ formatNumber(summary.asleep_hour) }}</span>
              <span class="unit">小時</span>
            </div>
          </div>
          <div class="stat-row" style="grid-template-columns: 84px 1fr;">
            <div class="label">上床時間：</div>
            <div class="input-look">{{ formatDateTimeCN(summary.asleep_start) }}</div>
          </div>
          <div class="stat-row" style="grid-template-columns: 84px 1fr;">
            <div class="label">起床時間：</div>
            <div class="input-look">{{ formatDateTimeCN(summary.asleep_end) }}</div>
          </div>
          <div class="stat-row">
            <div class="label">期間離床次數：</div>
            <div>
              <span :class="['value-lg', leaveCountColor]">{{ summary.asleep_leave != null ? summary.asleep_leave : '-' }}</span>
              <span class="unit">次</span>
            </div>
          </div>
          <div class="stat-row">
            <div class="label">期間離床時長：</div>
            <div>
              <span :class="['value-lg', leaveMinuteColor]">{{ formatNumber(summary.asleep_leave_minute) }}</span>
              <span class="unit">分鐘</span>
            </div>
          </div>
          <div class="stat-row">
            <div class="label">休息效率：</div>
            <div>
              <span class="value-xl num-dark">{{ summary.sleep_efficiency != null ? summary.sleep_efficiency + '%' : '-' }}</span>
            </div>
          </div>

        </div>

        <hr class="ddiv"/>

        <!-- Breathing card -->
        <div class="gcard tight">
          <div class="pill-row" style="justify-content:center;">
            <div class="pill cyan" @click="currentView = 'breathing'">呼吸詳細</div>
            <svg class="hand" style="left:108px; top:30px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
            <div class="know-pill" style="top:8px;" @click="currentView = 'breathingTips'">呼吸率<br/>小知識</div>
            <svg class="hand" style="right:6px; top:48px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="stat-row" style="grid-template-columns: 160px 1fr;">
            <div class="label">平均休息呼吸率：</div>
            <div>
              <span class="value-lg num-dark">{{ formatNumber(summary.sleep_respiration) }}</span>
              <span class="unit">次/分</span>
            </div>
          </div>
        </div>

        <hr class="ddiv"/>

        <!-- Heart rate card -->
        <div class="gcard tight">
          <div class="pill-row" style="justify-content:center;">
            <div class="pill cyan" @click="currentView = 'heartRate'">心率詳細</div>
            <svg class="hand" style="left:108px; top:30px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="stat-row" style="grid-template-columns: 110px 1fr;">
            <div class="label">平均心率：</div>
            <div style="text-align:right; padding-right:20px;">
              <span class="value-lg num-dark">{{ heartRate && heartRate.avg ? formatNumber(heartRate.avg) : '-' }}</span>
              <span class="unit">次/分</span>
            </div>
          </div>
        </div>

        <hr class="ddiv"/>

        <!-- Turning card -->
        <div class="gcard" v-if="turnover && turnover.total_count != null">
          <div class="pill-row" style="justify-content:center;">
            <div class="pill cyan" @click="currentView = 'turnover'">翻身分析</div>
            <svg class="hand" style="left:108px; top:30px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="stat-row" style="grid-template-columns: 140px 1fr;">
            <div class="label">昨日翻身次數：</div>
            <div>
              <span :class="['value-lg', turnoverCountColor]">{{ turnover.yesterday_count != null ? turnover.yesterday_count : turnover.total_count }}</span>
              <span class="unit">次</span>
            </div>
          </div>
          <div class="stat-row" style="grid-template-columns: 140px 1fr;">
            <div class="label">平均翻身間隔：</div>
            <div>
              <span class="value-lg num-dark">{{ formatNumber(turnover.avg_interval_min) }}</span>
              <span class="unit">分鐘</span>
            </div>
          </div>
          <div class="stat-row" style="grid-template-columns: 140px 1fr;">
            <div class="label">最長翻身間隔：</div>
            <div>
              <span class="value-lg num-dark">{{ formatNumber(turnover.max_interval_min) }}</span>
              <span class="unit">分鐘</span>
            </div>
          </div>
        </div>

      </template>

      <!-- ============ SCREEN 2 : WEEKLY ============ -->
      <template v-else-if="reportData && currentView === 'weekly'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">一週休息分析</div>
        </div>

        <div class="sec-title"><span class="kw">一週</span>夜間休息與在離床分析：</div>
        <div class="chart-card" v-if="weeklyBarChartData">
          <div class="legend">
            <span><span class="sw" style="background:#3f6fcf;"></span>(夜)在床時長</span>
            <span><span class="sw" style="background:#62b1bf;"></span>(日)在床時長</span>
            <span><span class="sw sw-dot" style="background:#222;"></span>夜間離床次數</span>
            <span><span class="sw" style="background:#e89c4a;"></span>(夜)休息時長</span>
          </div>
          <svg viewBox="0 0 400 200" width="100%" height="200" aria-hidden="true">
            <!-- gridlines -->
            <g stroke="#cfcfcf" stroke-width="1">
              <line x1="40" y1="20" x2="395" y2="20"/>
              <line x1="40" y1="50" x2="395" y2="50"/>
              <line x1="40" y1="80" x2="395" y2="80"/>
              <line x1="40" y1="110" x2="395" y2="110"/>
              <line x1="40" y1="140" x2="395" y2="140"/>
              <line x1="40" y1="170" x2="395" y2="170"/>
            </g>
            <!-- y labels -->
            <g font-size="10" fill="#555">
              <text x="0" y="8">小時</text>
              <text x="22" y="24">24</text>
              <text x="22" y="54">18</text>
              <text x="22" y="84">12</text>
              <text x="28" y="114">6</text>
              <text x="28" y="144">3</text>
              <text x="28" y="173">0</text>
            </g>
            <!-- bars -->
            <g v-for="(bar, idx) in weeklyBarChartData.bars" :key="'wb'+idx">
              <rect :x="bar.x" :y="bar.nightY" width="22" :height="bar.nightH" fill="#3f6fcf"/>
              <rect :x="bar.x" :y="bar.dayY" width="22" :height="bar.dayH" fill="#62b1bf"/>
            </g>
            <!-- Orange rest line -->
            <polyline :points="weeklyBarChartData.sleepLine" fill="none" stroke="#e89c4a" stroke-width="2"/>
            <g fill="#e89c4a">
              <circle v-for="(pt, idx) in weeklyBarChartData.sleepDots" :key="'wsd'+idx" :cx="pt.x" :cy="pt.y" r="3"/>
            </g>
            <g font-size="13" fill="#e89c4a" font-weight="700" text-anchor="middle">
              <text v-for="(pt, idx) in weeklyBarChartData.sleepDots" :key="'wsl'+idx" :x="pt.x" :y="pt.y - 10">{{ pt.label }}</text>
            </g>
            <!-- leave count dots -->
            <g fill="#1c1c1c">
              <circle v-for="(bar, idx) in weeklyBarChartData.bars" :key="'wld'+idx" :cx="bar.cx" cy="170" r="3.5"/>
            </g>
            <g font-size="14" fill="#1c1c1c" font-weight="700" text-anchor="middle">
              <text v-for="(bar, idx) in weeklyBarChartData.bars" :key="'wlv'+idx" :x="bar.cx" y="162">{{ bar.leaveCount }}</text>
            </g>
            <!-- x labels -->
            <g font-size="11" fill="#222" text-anchor="middle">
              <text v-for="(bar, idx) in weeklyBarChartData.bars" :key="'wxl'+idx" :x="bar.cx" :y="idx % 2 === 0 ? 186 : 196">{{ bar.dateLabel }}</text>
            </g>
          </svg>
        </div>

        <div class="sec-title blue"><span class="kw">一週</span>休息呼吸率 (次/分鐘)：</div>
        <div class="chart-card" v-if="weeklyBreathingLineData">
          <svg viewBox="0 0 400 200" width="100%" height="200" aria-hidden="true">
            <g stroke="#cfcfcf" stroke-width="1">
              <line x1="40" y1="20" x2="395" y2="20"/>
              <line x1="40" y1="45" x2="395" y2="45"/>
              <line x1="40" y1="70" x2="395" y2="70"/>
              <line x1="40" y1="95" x2="395" y2="95"/>
              <line x1="40" y1="120" x2="395" y2="120"/>
              <line x1="40" y1="145" x2="395" y2="145"/>
              <line x1="40" y1="170" x2="395" y2="170"/>
            </g>
            <g font-size="10" fill="#555">
              <text x="0" y="8">次/分鐘</text>
              <text x="22" y="24">44</text>
              <text x="22" y="49">38</text>
              <text x="22" y="74">32</text>
              <text x="22" y="99">26</text>
              <text x="22" y="124">20</text>
              <text x="22" y="149">14</text>
              <text x="28" y="173">8</text>
            </g>
            <polyline :points="weeklyBreathingLineData.line" fill="none" stroke="#4080d9" stroke-width="2"/>
            <g fill="#4080d9">
              <circle v-for="(pt, idx) in weeklyBreathingLineData.dots" :key="'wbc'+idx" :cx="pt.x" :cy="pt.y" r="3.5"/>
            </g>
            <g font-size="12" fill="#4080d9" font-weight="600" text-anchor="middle">
              <text v-for="(pt, idx) in weeklyBreathingLineData.dots" :key="'wbt'+idx" :x="pt.x" :y="pt.y - 9">{{ pt.label }}</text>
            </g>
            <g font-size="11" fill="#222" text-anchor="middle">
              <text v-for="(pt, idx) in weeklyBreathingLineData.dots" :key="'wbx'+idx" :x="pt.x" :y="idx % 2 === 0 ? 186 : 196">{{ pt.dateLabel }}</text>
            </g>
          </svg>
        </div>

        <table class="data" v-if="weeklyTrend && weeklyTrend.length">
          <thead>
            <tr>
              <th>日期</th>
              <th>上床時間</th>
              <th>起床時間</th>
              <th>休息期間<br/>離床次數</th>
              <th>休息期間<br/>離床分鐘</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in weeklyTrendReversed" :key="day.date">
              <td>{{ formatShortDate(day.date) }}</td>
              <td>{{ formatTableTime(day.asleep_start) }}</td>
              <td>{{ formatTableTime(day.asleep_end) }}</td>
              <td align="right">{{ day.asleep_leave }}</td>
              <td align="right">{{ formatNumber(day.asleep_leave_minute) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="pager" v-if="weeklyTrend && weeklyTrend.length">
          1 - {{ weeklyTrend.length }} / {{ weeklyTrend.length }}
          <span class="arrow">&lsaquo;</span>
          <span class="arrow">&rsaquo;</span>
        </div>
      </template>

      <!-- ============ SCREEN 3 : HOURLY ============ -->
      <template v-else-if="reportData && currentView === 'hourly'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">分時資料</div>
        </div>

        <div style="height:8px"></div>

        <div class="input-row">
          <div class="label">上床時間：</div>
          <div class="input-look">{{ formatDateTimeCN(summary.asleep_start) }}</div>
        </div>
        <div class="input-row">
          <div class="label">起床時間：</div>
          <div class="input-look">{{ formatDateTimeCN(summary.asleep_end) }}</div>
        </div>

        <div class="color-legend">
          <div class="clg-grid">
            <div>顏色說明：</div>
            <div><span class="sw-clg" style="background:#3f6fcf;"></span>在床</div>
            <div><span class="sw-clg" style="background:#7fbf7f;"></span>在床休息</div>
            <div></div>
            <div><span class="sw-clg" style="background:#bdbdbd;"></span>不在床</div>
            <div><span class="sw-clg" style="background:#fff;"></span>無資料</div>
          </div>
        </div>

        <div class="timeline-wrap" v-if="bedTimelinePanels && bedTimelinePanels.length">
          <div v-for="(panel, pi) in bedTimelinePanels" :key="'bt'+pi" class="timeline" :style="pi > 0 ? 'margin-top:18px;' : ''">
            <div :class="['tl-bar', panel.isNight ? 'night' : 'day']">
              <div class="tl-icon" :style="panel.isNight ? 'background:linear-gradient(180deg,#33368a,#1b1c4a);' : 'background:linear-gradient(180deg,#f8c84a,#e6a836);'">
                <!-- Sun icon -->
                <svg v-if="!panel.isNight" viewBox="0 0 24 24" width="22" height="22" fill="#fff8d2" stroke="#b27a1a" stroke-width="1.2">
                  <circle cx="12" cy="12" r="4.5"/>
                  <g stroke="#b27a1a" stroke-width="1.4">
                    <line x1="12" y1="3" x2="12" y2="5.5"/><line x1="12" y1="18.5" x2="12" y2="21"/>
                    <line x1="3" y1="12" x2="5.5" y2="12"/><line x1="18.5" y1="12" x2="21" y2="12"/>
                    <line x1="5.5" y1="5.5" x2="7.3" y2="7.3"/><line x1="16.7" y1="16.7" x2="18.5" y2="18.5"/>
                    <line x1="18.5" y1="5.5" x2="16.7" y2="7.3"/><line x1="7.3" y1="16.7" x2="5.5" y2="18.5"/>
                  </g>
                </svg>
                <!-- Moon icon -->
                <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="#ffe89c">
                  <path d="M19 14a8 8 0 1 1-9-9 6.5 6.5 0 0 0 9 9z"/>
                </svg>
              </div>
            </div>
            <div class="tl-data" :style="panel.isEmpty ? 'background:#fff; border:1px solid #ddd;' : ''">
              <template v-if="!panel.isEmpty">
                <div v-for="(seg, si) in panel.segments" :key="'s'+pi+'_'+si" :style="{ background: seg.color, flex: seg.flex }"></div>
              </template>
            </div>
            <div class="tl-axis">
              <span v-for="(lbl, li) in panel.xLabels" :key="'xl'+pi+'_'+li" :class="li === 0 || li === panel.xLabels.length - 1 ? 'lbl' : ''">{{ lbl }}</span>
            </div>
            <div style="display:flex; justify-content:space-between; font-size:11px; color:#222; margin-top:14px;">
              <span>{{ panel.dateStart }}</span>
              <span v-if="panel.isNight" :style="'margin-left:34%;'">{{ panel.dateEnd }}</span>
              <span v-if="panel.isNight"></span>
              <span v-if="!panel.isNight">{{ panel.dateEnd }}</span>
            </div>
          </div>
        </div>

        <div style="margin-top:20px;" v-if="leaveDetail && leaveDetail.length">
          <div class="sec-title" style="font-size:16px; font-weight:700;">離床詳細資料：(近兩日)</div>
          <table class="data" style="margin-top:8px;">
            <thead>
              <tr>
                <th></th>
                <th style="color:#4080d9;">離床時間</th>
                <th style="color:#4080d9;">離床時間 (分鐘)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in leaveDetail" :key="'ld'+idx">
                <td>{{ idx + 1 }}.</td>
                <td>{{ formatDateTimeCN(item.time) }}</td>
                <td align="right">{{ item.duration_min }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- ============ SCREEN 4 : BREATHING DETAIL ============ -->
      <template v-else-if="reportData && currentView === 'breathing'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">呼吸詳細</div>
        </div>

        <div class="br-range">
          <span>{{ detailStartDate }}</span><span class="t">12:00</span>
          <span>~</span>
          <span>{{ detailEndDate }}</span><span class="t">12:00</span>
        </div>
        <div style="display:flex; align-items:baseline; gap:6px; justify-content:center; padding:6px 0 12px;">
          <span style="font-size:18px;">休息呼吸率：</span>
          <span class="value-xl num-dark">{{ formatNumber(summary.sleep_respiration) }}</span>
          <span class="unit">次/分</span>
        </div>

        <hr class="ddiv"/>

        <div class="gcard tight" style="margin-top:0;" v-if="respiration && respiration.avg">
          <div style="font-size:16px; padding:4px 4px 8px;">
            {{ detailStartDate }} <span style="color:#4080d9; font-size:13px;">06:00 ~ 現在</span>
          </div>
          <div class="br-stat-grid">
            <div class="label">最高呼吸率：</div>
            <div class="v num-red">{{ formatNumber(respiration.max) }}</div>
            <div class="label">最低呼吸率：</div>
            <div class="v num-dark">{{ formatNumber(respiration.min) }}</div>
            <div class="label">呼吸率中位數：</div>
            <div class="v num-dark">{{ breathingMedian }}</div>
          </div>
          <div style="height:8px;"></div>
          <div class="br-stat-grid">
            <div class="label">平均呼吸率：</div>
            <div class="v num-dark">{{ formatNumber(respiration.avg) }}</div>
            <div class="label">標準差：</div>
            <div class="v num-dark">{{ formatNumber(respiration.stddev) }}</div>
          </div>
        </div>

        <div class="br-mini-legend">
          <span><span class="dot"></span>呼吸率 次/分鐘</span>
          <span><span class="ln"></span>呼吸趨勢線</span>
        </div>

        <div class="scatter-wrap" v-if="breathingScatter && breathingScatter.length">
          <div v-for="(panel, pi) in breathingPanels" :key="'bp'+pi" class="scatter-block">
            <!-- Day strip -->
            <div v-if="!panel.isNight" style="height:18px; background:linear-gradient(90deg,#e6a836,#f5cf69 50%,#e6a836); position:relative;">
              <div style="position:absolute; left:50%; top:-4px; transform:translateX(-50%); width:26px; height:26px; background:linear-gradient(180deg,#f8c84a,#e6a836); border-radius:6px; display:flex; align-items:center; justify-content:center;">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff8d2" stroke="#b27a1a" stroke-width="1.2">
                  <circle cx="12" cy="12" r="4.5"/>
                  <g stroke="#b27a1a" stroke-width="1.4">
                    <line x1="12" y1="3" x2="12" y2="5.5"/><line x1="12" y1="18.5" x2="12" y2="21"/>
                    <line x1="3" y1="12" x2="5.5" y2="12"/><line x1="18.5" y1="12" x2="21" y2="12"/>
                  </g>
                </svg>
              </div>
            </div>
            <!-- Night strip -->
            <div v-else style="height:18px; background:linear-gradient(90deg,#7d83cc,#3f3f9c 50%,#7d83cc); position:relative;">
              <div style="position:absolute; left:50%; top:-4px; transform:translateX(-50%); width:26px; height:26px; background:#1b1c4a; border-radius:6px; display:flex; align-items:center; justify-content:center;">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#ffe89c">
                  <path d="M19 14a8 8 0 1 1-9-9 6.5 6.5 0 0 0 9 9z"/>
                </svg>
              </div>
            </div>
            <svg viewBox="0 0 340 180" width="100%" height="180" aria-hidden="true">
              <g stroke="#dadada" stroke-width="1">
                <line x1="36" y1="12" x2="335" y2="12"/>
                <line x1="36" y1="32" x2="335" y2="32"/>
                <line x1="36" y1="52" x2="335" y2="52"/>
                <line x1="36" y1="72" x2="335" y2="72"/>
                <line x1="36" y1="92" x2="335" y2="92"/>
                <line x1="36" y1="112" x2="335" y2="112"/>
                <line x1="36" y1="132" x2="335" y2="132"/>
              </g>
              <g font-size="10" fill="#444">
                <text x="14" y="16">44</text><text x="14" y="36">38</text>
                <text x="14" y="56">32</text><text x="14" y="76">26</text>
                <text x="14" y="96">20</text><text x="14" y="116">14</text>
                <text x="22" y="136">8</text>
              </g>
              <!-- trend line -->
              <line v-if="panel.trendLine" :x1="panel.trendLine.x1" :y1="panel.trendLine.y1" :x2="panel.trendLine.x2" :y2="panel.trendLine.y2" stroke="#cf6b87" stroke-width="1.5"/>
              <!-- dots -->
              <g fill="#4080d9">
                <circle v-for="(dot, di) in panel.dots" :key="'sd'+di" :cx="dot.x" :cy="dot.y" r="2.6"/>
              </g>
              <!-- x axis labels -->
              <g font-size="11" fill="#222" text-anchor="middle">
                <text v-for="(lbl, li) in panel.xAxisLabels" :key="'sxl'+li" :x="lbl.x" y="150">{{ lbl.text }}</text>
              </g>
              <g font-size="11" fill="#222">
                <text :x="40" y="168">{{ panel.dateStart }}</text>
                <text :x="panel.isNight ? 170 : 290" y="168">{{ panel.dateEnd }}</text>
              </g>
            </svg>
          </div>
        </div>
      </template>

      <!-- ============ SCREEN 5 : HEART RATE DETAIL ============ -->
      <template v-else-if="reportData && currentView === 'heartRate'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">心率詳細</div>
        </div>

        <div class="br-range">
          <span>{{ detailStartDate }}</span><span class="t">12:00</span>
          <span>~</span>
          <span>{{ detailEndDate }}</span><span class="t">12:00</span>
        </div>
        <div style="display:flex; align-items:baseline; gap:6px; justify-content:center; padding:6px 0 12px;">
          <span style="font-size:18px;">平均心率：</span>
          <span class="value-xl num-dark">{{ heartRate && heartRate.avg ? formatNumber(heartRate.avg) : '-' }}</span>
          <span class="unit">次/分</span>
        </div>

        <hr class="ddiv"/>

        <div class="gcard tight" style="margin-top:0;" v-if="heartRate && heartRate.avg">
          <div style="font-size:16px; padding:4px 4px 8px;">
            {{ detailStartDate }} <span style="color:#4080d9; font-size:13px;">06:00 ~ 現在</span>
          </div>
          <div class="br-stat-grid">
            <div class="label">最高心率：</div>
            <div class="v num-red">{{ formatNumber(heartRate.max) }}</div>
            <div class="label">最低心率：</div>
            <div class="v num-dark">{{ formatNumber(heartRate.min) }}</div>
          </div>
          <div style="height:8px;"></div>
          <div class="br-stat-grid">
            <div class="label">平均心率：</div>
            <div class="v num-dark">{{ formatNumber(heartRate.avg) }}</div>
            <div class="label">標準差：</div>
            <div class="v num-dark">{{ formatNumber(heartRate.stddev) }}</div>
          </div>
        </div>

        <div class="br-mini-legend">
          <span><span class="dot" style="background:#e57373;"></span>心率 次/分鐘</span>
          <span><span class="ln"></span>心率趨勢線</span>
        </div>

        <div class="scatter-wrap" v-if="heartRateScatter && heartRateScatter.length">
          <div v-for="(panel, pi) in heartRatePanels" :key="'hp'+pi" class="scatter-block">
            <!-- Day strip -->
            <div v-if="!panel.isNight" style="height:18px; background:linear-gradient(90deg,#e6a836,#f5cf69 50%,#e6a836); position:relative;">
              <div style="position:absolute; left:50%; top:-4px; transform:translateX(-50%); width:26px; height:26px; background:linear-gradient(180deg,#f8c84a,#e6a836); border-radius:6px; display:flex; align-items:center; justify-content:center;">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff8d2" stroke="#b27a1a" stroke-width="1.2">
                  <circle cx="12" cy="12" r="4.5"/>
                  <g stroke="#b27a1a" stroke-width="1.4">
                    <line x1="12" y1="3" x2="12" y2="5.5"/><line x1="12" y1="18.5" x2="12" y2="21"/>
                    <line x1="3" y1="12" x2="5.5" y2="12"/><line x1="18.5" y1="12" x2="21" y2="12"/>
                  </g>
                </svg>
              </div>
            </div>
            <!-- Night strip -->
            <div v-else style="height:18px; background:linear-gradient(90deg,#7d83cc,#3f3f9c 50%,#7d83cc); position:relative;">
              <div style="position:absolute; left:50%; top:-4px; transform:translateX(-50%); width:26px; height:26px; background:#1b1c4a; border-radius:6px; display:flex; align-items:center; justify-content:center;">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#ffe89c">
                  <path d="M19 14a8 8 0 1 1-9-9 6.5 6.5 0 0 0 9 9z"/>
                </svg>
              </div>
            </div>
            <svg viewBox="0 0 340 180" width="100%" height="180" aria-hidden="true">
              <g stroke="#dadada" stroke-width="1">
                <line x1="36" y1="12" x2="335" y2="12"/>
                <line x1="36" y1="36" x2="335" y2="36"/>
                <line x1="36" y1="60" x2="335" y2="60"/>
                <line x1="36" y1="84" x2="335" y2="84"/>
                <line x1="36" y1="108" x2="335" y2="108"/>
                <line x1="36" y1="132" x2="335" y2="132"/>
              </g>
              <g font-size="10" fill="#444">
                <text x="14" y="16">180</text><text x="14" y="40">150</text>
                <text x="14" y="64">120</text><text x="14" y="88">90</text>
                <text x="14" y="112">60</text><text x="14" y="136">30</text>
              </g>
              <!-- trend line -->
              <line v-if="panel.trendLine" :x1="panel.trendLine.x1" :y1="panel.trendLine.y1" :x2="panel.trendLine.x2" :y2="panel.trendLine.y2" stroke="#cf6b87" stroke-width="1.5"/>
              <!-- dots -->
              <g fill="#e57373">
                <circle v-for="(dot, di) in panel.dots" :key="'hd'+di" :cx="dot.x" :cy="dot.y" r="2.6"/>
              </g>
              <!-- x axis labels -->
              <g font-size="11" fill="#222" text-anchor="middle">
                <text v-for="(lbl, li) in panel.xAxisLabels" :key="'hxl'+li" :x="lbl.x" y="150">{{ lbl.text }}</text>
              </g>
              <g font-size="11" fill="#222">
                <text :x="40" y="168">{{ panel.dateStart }}</text>
                <text :x="panel.isNight ? 170 : 290" y="168">{{ panel.dateEnd }}</text>
              </g>
            </svg>
          </div>
        </div>
      </template>

      <!-- ============ SCREEN 6 : TURNOVER DETAIL ============ -->
      <template v-else-if="reportData && currentView === 'turnover'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">翻身分析</div>
        </div>

        <div class="sec-title"><span class="kw">一週</span>翻身次數與平均間隔時間：</div>
        <div class="chart-card" v-if="weeklyTurnover && weeklyTurnover.length">
          <div class="legend">
            <span><span class="sw sw-dot" style="background:#4080d9;"></span>平均翻身間隔(分鐘)</span>
            <span><span class="sw" style="background:#62b1bf;"></span>翻身次數</span>
          </div>
          <svg viewBox="0 0 400 200" width="100%" height="200" aria-hidden="true">
            <g stroke="#cfcfcf" stroke-width="1">
              <line x1="40" y1="20" x2="395" y2="20"/>
              <line x1="40" y1="50" x2="395" y2="50"/>
              <line x1="40" y1="80" x2="395" y2="80"/>
              <line x1="40" y1="110" x2="395" y2="110"/>
              <line x1="40" y1="140" x2="395" y2="140"/>
              <line x1="40" y1="170" x2="395" y2="170"/>
            </g>
            <g font-size="10" fill="#555" text-anchor="end">
              <text v-for="(yl, idx) in turnoverChartData.yLabels" :key="'ty'+idx" x="36" :y="yl.y + 3">{{ yl.text }}</text>
            </g>
            <g v-for="(item, idx) in turnoverChartData.bars" :key="'tb'+idx">
              <rect :x="item.x" :y="item.barY" width="22" :height="item.barH" fill="#62b1bf"/>
              <text :x="item.cx" :y="item.barY - 4" font-size="12" fill="#62b1bf" font-weight="700" text-anchor="middle">{{ item.count }}</text>
            </g>
            <polyline :points="turnoverChartData.intervalLine" fill="none" stroke="#4080d9" stroke-width="2"/>
            <g fill="#4080d9">
              <circle v-for="(item, idx) in turnoverChartData.bars" :key="'td'+idx" :cx="item.cx" :cy="item.dotY" r="3.5"/>
            </g>
            <g font-size="11" fill="#4080d9" font-weight="600" text-anchor="middle">
              <text v-for="(item, idx) in turnoverChartData.bars" :key="'tt'+idx" :x="item.cx" :y="item.dotY - 8">{{ item.interval != null ? item.interval : '' }}</text>
            </g>
            <g font-size="11" fill="#222" text-anchor="middle">
              <text v-for="(item, idx) in turnoverChartData.bars" :key="'txl'+idx" :x="item.cx" :y="idx % 2 === 0 ? 186 : 196">{{ item.dateLabel }}</text>
            </g>
          </svg>
        </div>

        <div class="sec-title" style="margin-top:18px; font-size:16px; font-weight:700;">詳細資料：(近兩日)</div>
        <table class="data" style="margin-top:8px;" v-if="turnover && turnover.hourly && turnover.hourly.length">
          <thead>
            <tr>
              <th></th>
              <th style="color:#4080d9;">月/日</th>
              <th style="color:#4080d9;">時 (24H)</th>
              <th style="color:#4080d9;">翻身次數</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in turnover.hourly" :key="'th'+idx">
              <td>{{ idx + 1 }}.</td>
              <td>{{ formatShortDate(item.date) }}</td>
              <td>{{ item.hour }}</td>
              <td align="right">{{ item.count }}</td>
            </tr>
          </tbody>
        </table>
        <div class="pager" v-if="turnover && turnover.hourly && turnover.hourly.length">
          1 - {{ turnover.hourly.length }} / {{ turnover.hourly.length }}
          <span class="arrow">&lsaquo;</span>
          <span class="arrow">&rsaquo;</span>
        </div>
      </template>

      <!-- ============ SCREEN 7 : SLEEP TIPS ============ -->
      <template v-else-if="reportData && currentView === 'sleepTips'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">睡眠小知識</div>
        </div>

        <div class="tips-section">
          <!-- Banner -->
          <a class="tips-banner pink" href="https://health99.hpa.gov.tw/material/3246" target="_blank" rel="noopener">
            衛福部 - 睡眠與精神健康
          </a>

          <!-- 海報 + 衛教單張 -->
          <div class="tips-poster-row">
            <div class="tips-poster" @click="tipModalImg = '/images/sleep-tips/poster-sleep-know.png'">
              <img src="/images/sleep-tips/poster-sleep-know.png" alt="睡眠知多少" />
            </div>
            <div class="tips-links">
              <div class="tips-links-title">衛教單張</div>
              <div class="tips-thumb" @click="tipModalImg = '/images/sleep-tips/sleep-know-1.jpg'">
                <span>睡眠知多少-1</span>
              </div>
              <div class="tips-thumb" @click="tipModalImg = '/images/sleep-tips/sleep-know-2.jpg'">
                <span>睡眠知多少-2</span>
              </div>
              <div class="tips-thumb" @click="tipModalImg = '/images/sleep-tips/sleep-med-1.jpg'">
                <span>睡眠用藥知多少-1</span>
              </div>
              <div class="tips-thumb" @click="tipModalImg = '/images/sleep-tips/sleep-med-2.jpg'">
                <span>睡眠用藥知多少-2</span>
              </div>
            </div>
          </div>

          <!-- 一天睡多少才夠 -->
          <div class="tips-chart-section">
            <div class="tips-subtitle">一天睡多少才夠</div>
            <img class="tips-full-img" src="/images/sleep-tips/chart-sleep-hours.jpg" alt="一天睡多少才夠" @click="tipModalImg = '/images/sleep-tips/chart-sleep-hours.jpg'" />
          </div>

          <!-- 健康睡眠的基本法則 -->
          <div class="tips-rules-section">
            <img class="tips-full-img" src="/images/sleep-tips/section-sleep-rules.jpg" alt="健康睡眠的基本法則" @click="tipModalImg = '/images/sleep-tips/section-sleep-rules.jpg'" />
            <div class="tips-rules-cols">
              <img src="/images/sleep-tips/rules-night.jpg" alt="夜晚6要遵守" @click="tipModalImg = '/images/sleep-tips/rules-night.jpg'" />
              <img src="/images/sleep-tips/rules-daily.jpg" alt="日常生活6要遵守" @click="tipModalImg = '/images/sleep-tips/rules-daily.jpg'" />
            </div>
          </div>
        </div>

        <div class="tip-modal-overlay" v-if="tipModalImg" @click="tipModalImg = null">
          <div class="tip-modal-content" @click.stop>
            <button class="tip-modal-close" @click="tipModalImg = null">&times;</button>
            <img :src="tipModalImg" class="tip-modal-img" />
          </div>
        </div>
      </template>

      <!-- ============ SCREEN 8 : BREATHING TIPS ============ -->
      <template v-else-if="reportData && currentView === 'breathingTips'">
        <div class="top-header">
          <div class="back-pill" @click="currentView = 'main'">回首頁
            <svg class="hand" style="left:24px; top:24px;" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 11.5V8a1.5 1.5 0 0 1 3 0v9c0 3.5-2.5 6-6 6H12c-2 0-3-1-4.5-3l-3-4.5c-.8-1.2-.4-2.5.8-2.9 1.2-.4 2.5.2 3.7 1.8L9 16V5.5a1.5 1.5 0 0 1 3 0V11M12 11V4.5a1.5 1.5 0 0 1 3 0V11M15 11V6a1.5 1.5 0 0 1 3 0v8" stroke="#222" stroke-width="1.4" fill="#fff"/>
            </svg>
          </div>
          <div class="page-title">呼吸率小知識</div>
        </div>

        <div class="tips-section">
          <a class="tips-banner blue" href="http://www.chimei.org.tw/main/cmh_department/59012/info/D300/AD300110.html" target="_blank" rel="noopener">
            奇美衛教資訊網
          </a>

          <div class="breathing-card">
            <p>生命徵象指<strong style="color:#1976d2">「體溫、脈搏、呼吸及血壓」</strong>，可反映病人的生理狀況。當病人生理發生異常時或有疾病徵兆時，生命徵象會產生異常變化。故生命徵象可作為病人健康狀態的指標，作為判斷、治療及居家照護的依據。照顧者需了解其變化，以做適當的判斷及處理。</p>

            <h4>首先，先評估病人意識狀態</h4>
            <p>您可以先呼叫病人，看看是否有反應，例如：「你叫什麼名字？」、「你聽的到我的聲音嗎？」，並確認以下情形。</p>
            <ol>
              <li><strong>清：</strong>是否清醒。</li>
              <li><strong>聲：</strong>對聲音刺激有反應，可以回答嗎？</li>
              <li><strong>痛：</strong>只對疼痛刺激（捏）有反應？</li>
              <li><strong>否：</strong>無任何反應。</li>
            </ol>
          </div>

          <div class="breathing-card">
            <div class="breathing-section-title green">正常值</div>
            <table class="breathing-table">
              <tbody>
                <tr><td class="label-cell">脈率</td><td>成人：16~20次/分<br/>老人：12~20次/分</td></tr>
                <tr><td class="label-cell">呼吸</td><td>成人：16~20次/分<br/>老人：12~20次/分</td></tr>
              </tbody>
            </table>
          </div>

          <div class="breathing-card">
            <div class="breathing-section-title orange">異常處理</div>
            <ol class="breathing-list">
              <li>疾病及藥物的影響：發燒、胸腔積水、使用剌激性藥物，如強心劑會使呼吸增快；急性感染、休克、腦壓增高（如腦瘤、腦出血等）或使用鎮靜劑、疼痛、麻醉劑等，呼吸會變慢。</li>
              <li>注意並記錄呼吸次數、深淺、難易、聲音及病人之臉色，檢查呼吸道是否暢通，有無異物阻塞，呼吸道是否有痰液阻塞而影響呼吸，需儘早協助清除，並可抬高床頭。</li>
              <li>若呼吸急促（困難），有異常呼吸音，應送醫處理。</li>
            </ol>
          </div>
        </div>
      </template>

      <div class="hcr-center" v-else-if="!loading">
        <p>此日期無報表資料</p>
      </div>
    </div>
  </div>
</template>

<script>
import { homeCareReport } from '@/http/api/homeCareReport.js'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'

export default {
  name: 'HomeCareReport',
  components: {
    VueCtkDateTimePicker: VueCtkDateTimePicker
  },
  data: function () {
    return {
      loading: true,
      error: null,
      reportData: null,
      selectedDate: null,
      currentView: 'main',
      tipModalImg: null
    }
  },
  computed: {
    token: function () {
      return this.$route.params.token
    },
    summary: function () {
      return (this.reportData && this.reportData.summary) || {}
    },
    turnover: function () {
      return (this.reportData && this.reportData.turnover_detail) || {}
    },
    respiration: function () {
      return (this.reportData && this.reportData.respiration_detail) || {}
    },
    heartRate: function () {
      return (this.reportData && this.reportData.heart_rate_detail) || {}
    },
    weeklyTrend: function () {
      return (this.reportData && this.reportData.weekly_trend) || []
    },
    weeklyTrendReversed: function () {
      if (!this.weeklyTrend || !this.weeklyTrend.length) {
        return []
      }
      return this.weeklyTrend.slice().reverse()
    },
    shortDate: function () {
      if (!this.reportData || !this.reportData.report_date) {
        return ''
      }
      var p = this.reportData.report_date.split('-')
      return parseInt(p[1]) + '月' + parseInt(p[2]) + '日'
    },
    sleepWindow: function () {
      if (this.reportData && this.reportData.sleep_window) {
        var s = this.reportData.sleep_window.start
        var e = this.reportData.sleep_window.end
        return (s ? s.substring(0, 5) : '19:00') + ' ~ ' + (e || '08:00:00')
      }
      return '19:00 ~ 08:00:00'
    },
    maxDate: function () {
      var d = new Date()
      d.setDate(d.getDate() - 1)
      return d.toISOString().substring(0, 10)
    },
    detailStartDate: function () {
      if (!this.reportData || !this.reportData.report_date) {
        return ''
      }
      var p = this.reportData.report_date.split('-')
      return parseInt(p[1]) + '月' + parseInt(p[2]) + '日'
    },
    detailEndDate: function () {
      if (!this.reportData || !this.reportData.report_date) {
        return ''
      }
      var d = new Date(this.reportData.report_date)
      d.setDate(d.getDate() + 1)
      return (d.getMonth() + 1) + '月' + d.getDate() + '日'
    },
    sleepHourColor: function () {
      var val = parseFloat(this.summary.asleep_hour)
      if (isNaN(val)) {
        return 'num-dark'
      }
      if (val >= 7) {
        return 'num-green'
      }
      if (val >= 5) {
        return 'num-yellow'
      }
      return 'num-red'
    },
    leaveCountColor: function () {
      var val = parseInt(this.summary.asleep_leave)
      if (isNaN(val)) {
        return 'num-dark'
      }
      if (val <= 3) {
        return 'num-green'
      }
      if (val <= 6) {
        return 'num-yellow'
      }
      return 'num-red'
    },
    leaveMinuteColor: function () {
      var val = parseFloat(this.summary.asleep_leave_minute)
      if (isNaN(val)) {
        return 'num-dark'
      }
      if (val <= 30) {
        return 'num-green'
      }
      if (val <= 60) {
        return 'num-yellow'
      }
      return 'num-red'
    },
    turnoverCountColor: function () {
      var val = parseInt(this.turnover.yesterday_count != null ? this.turnover.yesterday_count : this.turnover.total_count)
      if (isNaN(val)) {
        return 'num-dark'
      }
      if (val >= 8) {
        return 'num-green'
      }
      if (val >= 4) {
        return 'num-yellow'
      }
      return 'num-red'
    },
    breathingScatter: function () {
      var d = this.respiration && this.respiration.scatter
      return (d && d.length) ? d.slice().sort(function (a, b) { return a.time - b.time }) : []
    },
    heartRateScatter: function () {
      var d = this.heartRate && this.heartRate.scatter
      return (d && d.length) ? d.slice().sort(function (a, b) { return a.time - b.time }) : []
    },
    breathingMedian: function () {
      var data = this.breathingScatter
      if (!data.length) {
        return '-'
      }
      var vals = data.map(function (d) { return d.value }).sort(function (a, b) { return a - b })
      var mid = Math.floor(vals.length / 2)
      return this.formatNumber(vals.length % 2 !== 0 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2)
    },
    breathingPanels: function () {
      return this.buildScatterPanels(this.breathingScatter, 8, 44)
    },
    heartRatePanels: function () {
      return this.buildScatterPanels(this.heartRateScatter, 30, 180)
    },

    /* ===== Weekly stacked bar chart data (design coordinate system) ===== */
    weeklyBarChartData: function () {
      var trend = this.weeklyTrend
      if (!trend || !trend.length) {
        return null
      }
      var n = trend.length
      var pxPerHr = 6.25
      var baseY = 170
      var spacing = 48
      var firstCx = 64
      var self = this

      var bars = []
      var sleepDots = []
      var sleepLineArr = []

      for (var i = 0; i < n; i++) {
        var nightBed = parseFloat(trend[i].night_on_bed) || 0
        var dayBed = parseFloat(trend[i].day_on_bed) || 0
        // (夜)休息時長 = asleep_hour，對齊舊版 Looker 與首頁摘要卡（原誤用 night_sleep）
        var nightSleep = parseFloat(trend[i].asleep_hour) || 0
        var nightLeave = trend[i].asleep_leave != null ? trend[i].asleep_leave : (trend[i].night_leave || 0)

        var cx = firstCx + i * spacing
        var bx = cx - 11
        var nightH = nightBed * pxPerHr
        var nightY = baseY - nightH
        var dayH = dayBed * pxPerHr
        var dayY = nightY - dayH
        var sleepY = baseY - (nightSleep * pxPerHr)

        bars.push({
          x: bx,
          cx: cx,
          nightY: nightY,
          nightH: nightH,
          dayY: dayY,
          dayH: dayH,
          leaveCount: nightLeave,
          dateLabel: self.formatShortDate(trend[i].date)
        })

        sleepDots.push({
          x: cx,
          y: sleepY,
          label: self.formatNumber(nightSleep)
        })
        sleepLineArr.push(cx + ',' + sleepY)
      }

      return {
        bars: bars,
        sleepDots: sleepDots,
        sleepLine: sleepLineArr.join(' ')
      }
    },

    /* ===== Weekly breathing line chart data (design coordinate system) ===== */
    weeklyBreathingLineData: function () {
      var trend = this.weeklyTrend
      if (!trend || !trend.length) {
        return null
      }
      var n = trend.length
      var pxPerUnit = 150 / 36
      var baseY = 170
      var minVal = 8
      var spacing = 48
      var firstCx = 64
      var self = this

      var dots = []
      var lineArr = []

      for (var i = 0; i < n; i++) {
        var val = parseFloat(trend[i].sleep_respiration) || 0
        var cx = firstCx + i * spacing
        var y = baseY - ((val - minVal) * pxPerUnit)
        if (y < 20) {
          y = 20
        }
        if (y > 170) {
          y = 170
        }

        dots.push({
          x: cx,
          y: y,
          label: self.formatNumber(val),
          dateLabel: self.formatShortDate(trend[i].date)
        })
        lineArr.push(cx + ',' + y)
      }

      return {
        dots: dots,
        line: lineArr.join(' ')
      }
    },

    /* ===== Bed timeline panels (hourly view) ===== */
    bedTimeline: function () {
      var tl = this.reportData && this.reportData.timeline
      if (!tl || !tl.bed_timeline) {
        return null
      }
      if (typeof tl.bed_timeline === 'string') {
        return tl.bed_timeline
      }
      if (tl.bed_timeline && tl.bed_timeline.values) {
        return tl.bed_timeline.values
      }
      return null
    },
    bedTimelinePanels: function () {
      var raw = this.bedTimeline
      if (!raw) {
        return []
      }
      var rd = this.reportData.report_date
      var panelBase = new Date(rd + 'T06:00:00+08:00')
      // 後端輸出 5 分鐘/格，12 小時 = 144 格
      var slotsPerPanel = 144
      var panelDefs = [
        { offset: 0, startH: 6 },
        { offset: slotsPerPanel, startH: 18 },
        { offset: slotsPerPanel * 2, startH: 6 },
        { offset: slotsPerPanel * 3, startH: 18 }
      ]
      var panels = []
      var self = this

      for (var p = 0; p < panelDefs.length; p++) {
        var def = panelDefs[p]
        var slotStart = def.offset
        var slotEnd = Math.min(slotStart + slotsPerPanel, raw.length)
        if (slotStart >= raw.length) break

        var panelStart = new Date(panelBase.getTime() + slotStart * 300000)
        var panelEnd = new Date(panelBase.getTime() + slotEnd * 300000)
        var isNight = (def.startH >= 18 || def.startH < 6)

        var segments = []
        var i = slotStart
        var isEmpty = true
        while (i < slotEnd) {
          var ch = raw[i]
          var type = self.bedCharToType(ch)
          if (type !== 'none') isEmpty = false
          var count = 0
          while (i < slotEnd && self.bedCharToType(raw[i]) === type) { count++; i++ }
          segments.push({ color: self.bedTypeToColor(type), flex: (count / slotsPerPanel).toFixed(4) })
        }

        // Post-process: enforce minimum visible width for small segments
        var minFlex = 2 / slotsPerPanel
        for (var si = 0; si < segments.length; si++) {
          var seg = segments[si]
          var segFlex = parseFloat(seg.flex)
          if ((seg.color === '#bdbdbd' || seg.color === '#fff') && segFlex > 0 && segFlex < minFlex) {
            var deficit = minFlex - segFlex
            var donor = null
            // Search left for a main segment to borrow from
            for (var li = si - 1; li >= 0; li--) {
              if (segments[li].color === '#7fbf7f' || segments[li].color === '#3f6fcf') {
                donor = segments[li]
                break
              }
            }
            // If not found left, search right
            if (!donor) {
              for (var ri = si + 1; ri < segments.length; ri++) {
                if (segments[ri].color === '#7fbf7f' || segments[ri].color === '#3f6fcf') {
                  donor = segments[ri]
                  break
                }
              }
            }
            if (donor && parseFloat(donor.flex) > deficit) {
              donor.flex = (parseFloat(donor.flex) - deficit).toFixed(4)
              seg.flex = minFlex.toFixed(4)
            }
          }
        }

        if (isEmpty) segments = []

        var xLabels = []
        for (var h = 0; h <= 12; h += 2) {
          var hr = (def.startH + h) % 24
          xLabels.push((hr < 10 ? '0' : '') + hr)
        }

        panels.push({
          isNight: isNight,
          isEmpty: isEmpty,
          segments: segments,
          xLabels: xLabels,
          dateStart: (panelStart.getMonth() + 1) + '月' + panelStart.getDate() + '日',
          dateEnd: (panelEnd.getMonth() + 1) + '月' + panelEnd.getDate() + '日'
        })
      }
      return panels
    },
    leaveDetail: function () {
      return (this.reportData && this.reportData.leave_detail) || []
    },
    weeklyTurnover: function () {
      return (this.reportData && this.reportData.weekly_turnover) || []
    },
    turnoverChartData: function () {
      var data = this.weeklyTurnover
      if (!data || !data.length) return null
      var n = data.length
      var baseY = 170
      // Y 軸動態上限：平均間隔可能超過 100（如 225），取 50 的倍數
      var maxV = 100
      for (var j = 0; j < n; j++) {
        var iv = data[j].avg_interval_min
        if (iv != null && iv > maxV) maxV = iv
        if ((data[j].count || 0) > maxV) maxV = data[j].count
      }
      var niceMax = Math.ceil(maxV / 50) * 50
      var pxPerUnit = 150 / niceMax
      var spacing = 48
      var firstCx = 64
      var self = this
      var bars = []
      var lineArr = []
      var yLabels = []
      for (var k = 0; k <= 5; k++) {
        yLabels.push({ y: 170 - k * 30, text: Math.round(niceMax * k / 5) })
      }
      for (var i = 0; i < n; i++) {
        var cx = firstCx + i * spacing
        var count = data[i].count || 0
        var interval = data[i].avg_interval_min
        var barH = count * pxPerUnit
        var barY = baseY - barH
        var dotY = interval != null ? baseY - (interval * pxPerUnit) : baseY
        bars.push({
          x: cx - 11,
          cx: cx,
          barY: barY,
          barH: barH,
          dotY: dotY,
          count: count,
          interval: interval,
          dateLabel: self.formatShortDate(data[i].date)
        })
        if (interval != null) lineArr.push(cx + ',' + dotY)
      }
      return { bars: bars, intervalLine: lineArr.join(' '), yLabels: yLabels }
    }
  },
  methods: {
    fetchReport: function (date) {
      this.loading = true
      this.error = null
      this.currentView = 'main'
      var self = this
      homeCareReport.getReport(this.token, date)
        .then(function (res) {
          if (res.data && res.data.status === 'success') {
            self.reportData = res.data.data
            self.selectedDate = self.reportData.report_date
          } else {
            self.error = (res.data && res.data.errMsg) || '無法載入報表'
          }
          self.loading = false
        })
        .catch(function (err) {
          if (err.response && err.response.status === 403) {
            self.error = '報表連結無效或已停用'
          } else {
            self.error = '載入失敗，請稍後再試'
          }
          self.loading = false
        })
    },
    onDateChange: function (val) {
      if (val && val !== (this.reportData && this.reportData.report_date)) {
        this.fetchReport(val)
      }
    },
    formatNumber: function (val) {
      if (val == null || val === '' || isNaN(val)) {
        return '-'
      }
      var n = parseFloat(val)
      if (n % 1 === 0) return String(Math.round(n))
      return String(Math.round(n * 10) / 10)
    },
    formatShortDate: function (val) {
      if (!val) {
        return '-'
      }
      var p = val.split('-')
      return parseInt(p[1]) + '月' + parseInt(p[2]) + '日'
    },
    formatTableTime: function (val) {
      if (!val || val === '-') {
        return '-'
      }
      try {
        var dt = new Date(val.indexOf(' ') > -1 ? val.replace(' ', 'T') : val)
        if (isNaN(dt.getTime())) {
          return val
        }
        var m = dt.getMonth() + 1
        var d = dt.getDate()
        var h = dt.getHours()
        var min = dt.getMinutes()
        return m + '月 ' + d + ', ' + (h < 10 ? '0' : '') + h + ':' + (min < 10 ? '0' : '') + min
      } catch (e) {
        return val
      }
    },
    formatDateTimeCN: function (val) {
      if (!val || val === '-') {
        return '-'
      }
      try {
        var dt = new Date(val.indexOf(' ') > -1 ? val.replace(' ', 'T') : val)
        if (isNaN(dt.getTime())) {
          return val
        }
        var y = dt.getFullYear()
        var m = dt.getMonth() + 1
        var d = dt.getDate()
        var h = dt.getHours()
        var min = dt.getMinutes()
        var period
        var h12 = h
        if (h < 6) {
          period = '清晨'
        } else if (h < 12) {
          period = '上午'
        } else if (h < 18) {
          period = '下午'
          h12 = h - 12
        } else {
          period = '晚上'
          h12 = h - 12
        }
        if (h12 === 0) {
          h12 = 12
        }
        return y + '年' + m + '月' + d + '日 ' + period + h12 + ':' + (min < 10 ? '0' + min : min)
      } catch (e) {
        return val
      }
    },
    bedCharToType: function (ch) {
      // 後端編碼：S=睡眠(00) T=坐姿(02) L=躺臥(07) V=離床(08) X=斷線 N=無資料
      if (ch === 'S') {
        return 'sleep'
      }
      if (ch === 'T' || ch === 'L' || ch === 'B') {
        return 'bed'
      }
      if (ch === 'V') {
        return 'leave'
      }
      if (ch === 'X' || ch === 'D' || ch === 'N' || ch === '-') {
        return 'none'
      }
      return 'bed'
    },
    bedTypeToColor: function (type) {
      if (type === 'sleep') {
        return '#7fbf7f'
      }
      if (type === 'bed') {
        return '#3f6fcf'
      }
      if (type === 'leave') {
        return '#bdbdbd'
      }
      return '#fff'
    },
    buildScatterPanels: function (scatter, minVal, maxVal) {
      if (!scatter || !scatter.length || !this.reportData) {
        return []
      }
      var rd = this.reportData.report_date
      var base = new Date(rd + 'T00:00:00+08:00')
      /* Design scatter coordinate system:
               viewBox="0 0 340 160"
               Y grid: 38@y=12, 32@y=36, 26@y=60, 20@y=84, 14@y=108, 8@y=132
               => minVal=8, maxVal=38, range=30
               => y = 132 - ((val - 8) / 30) * 120
               X range: 36 to 335, width=299
               Day panel: 06-18, Night panel: 18-06(+24), Day2 panel: 30-42 */
      var chartMinY = 12
      var chartMaxY = 132
      var chartH = chartMaxY - chartMinY
      var valRange = maxVal - minVal
      var xLeft = 40
      var xRight = 335
      var xRange = xRight - xLeft

      var segs = [
        {
          s: 6,
          e: 18,
          night: false
        },
        {
          s: 18,
          e: 30,
          night: true
        },
        {
          s: 30,
          e: 42,
          night: false
        }
      ]

      var panels = []

      for (var si = 0; si < segs.length; si++) {
        var seg = segs[si]
        var segStartTs = base.getTime() / 1000 + seg.s * 3600
        var segEndTs = base.getTime() / 1000 + seg.e * 3600
        var segDur = segEndTs - segStartTs
        var dots = []
        var trendPts = []

        for (var i = 0; i < scatter.length; i++) {
          var t = scatter[i].time
          var v = scatter[i].value
          if (t >= segStartTs && t < segEndTs) {
            var x = xLeft + ((t - segStartTs) / segDur) * xRange
            var y = chartMaxY - ((v - minVal) / valRange) * chartH
            if (y < chartMinY) {
              y = chartMinY
            }
            if (y > chartMaxY) {
              y = chartMaxY
            }
            dots.push({
              x: x.toFixed(1),
              y: y.toFixed(1)
            })
            trendPts.push({
              x: x,
              y: y,
              time: t,
              value: v
            })
          }
        }

        /* Build linear trend line */
        var trendLine = null
        if (trendPts.length > 2) {
          trendPts.sort(function (a, b) { return a.time - b.time })
          var n = trendPts.length
          var sumX = 0
          var sumY = 0
          var sumXY = 0
          var sumXX = 0
          for (var j = 0; j < n; j++) {
            sumX += trendPts[j].x
            sumY += trendPts[j].y
            sumXY += trendPts[j].x * trendPts[j].y
            sumXX += trendPts[j].x * trendPts[j].x
          }
          var denom = n * sumXX - sumX * sumX
          if (denom !== 0) {
            var slope = (n * sumXY - sumX * sumY) / denom
            var intercept = (sumY - slope * sumX) / n
            var ty1 = slope * xLeft + intercept
            var ty2 = slope * xRight + intercept
            if (ty1 < chartMinY) { ty1 = chartMinY }
            if (ty1 > chartMaxY) { ty1 = chartMaxY }
            if (ty2 < chartMinY) { ty2 = chartMinY }
            if (ty2 > chartMaxY) { ty2 = chartMaxY }
            trendLine = {
              x1: xLeft,
              y1: ty1.toFixed(1),
              x2: xRight,
              y2: ty2.toFixed(1)
            }
          }
        }

        /* X axis labels */
        var startH = seg.s % 24
        var xAxisLabels = []
        for (var xi = 0; xi <= 12; xi += 2) {
          var hr = (startH + xi) % 24
          var hrStr = (hr < 10 ? '0' : '') + hr
          /* Map label positions to design coordinates */
          var labelX = xLeft + (xi / 12) * xRange
          xAxisLabels.push({
            x: labelX.toFixed(0),
            text: hrStr
          })
        }

        var sd = new Date(base.getTime() + seg.s * 3600000)
        var ed = new Date(base.getTime() + seg.e * 3600000)

        panels.push({
          isNight: seg.night,
          dots: dots,
          trendLine: trendLine,
          xAxisLabels: xAxisLabels,
          dateStart: (sd.getMonth() + 1) + '月' + sd.getDate() + '日',
          dateEnd: (ed.getMonth() + 1) + '月' + ed.getDate() + '日'
        })
      }
      return panels
    }
  },
  mounted: function () {
    this.fetchReport(this.$route.query.date || null)
  }
}
</script>

<style scoped>
/* ===== Design System Variables ===== */
.hcr-wrap {
    --bg: #ffffff;
    --page: #fafafa;
    --card: #e9e9e9;
    --card-2: #ededed;
    --text: #2c2c2c;
    --muted: #7a7a7a;
    --green-pill: #bcd9b7;
    --beige-pill: #f1dcb2;
    --cyan-pill: #bfe6ed;
    --blue: #3b6cb4;
    --blue-text: #4080d9;
    --blue-bar: #3f6fcf;
    --teal-bar: #62b1bf;
    --orange: #e89c4a;
    --red: #b53a2c;
    --green: #3aa14a;
    --yellow: #e1a73b;
    --pink: #fbe2dc;
    --dot: #3f6fcf;
    --trend: #cf6b87;
}
.hcr-wrap {
    background: #f4f4f4;
    min-height: 100vh;
    padding: 18px 0;
    font-family: "PingFang TC", "Noto Sans TC", "Microsoft JhengHei", system-ui, sans-serif;
    color: var(--text);
}
.hcr-wrap * {
    box-sizing: border-box;
}
.hcr-screen {
    width: 380px;
    max-width: 100%;
    margin: 0 auto;
    background: var(--bg);
    border: 1px solid #e5e5e5;
    border-radius: 14px;
    padding: 18px 16px 22px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.04);
    position: relative;
}

/* ===== Shared pills / buttons ===== */
.pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 18px;
    border-radius: 14px;
    font-weight: 600;
    font-size: 16px;
    color: #222;
    border: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
}
.pill.cyan {
    background: var(--cyan-pill);
}
.pill.beige {
    background: var(--beige-pill);
}
.pill.pink {
    background: var(--pink);
}

.hand {
    position: absolute;
    width: 22px;
    height: 22px;
    pointer-events: none;
}

/* ===== Cards (gray rounded blocks) ===== */
.gcard {
    background: var(--card);
    border-radius: 12px;
    padding: 18px 16px;
    margin-top: 14px;
    position: relative;
}
.gcard.tight {
    padding: 14px 16px 16px;
}

/* ===== Utility ===== */
.label {
    font-size: 16px;
    color: #222;
}
.value-lg {
    font-size: 30px;
    font-weight: 600;
    letter-spacing: -0.5px;
}
.value-xl {
    font-size: 34px;
    font-weight: 600;
    letter-spacing: -1px;
}
.unit {
    font-size: 14px;
    color: #444;
    margin-left: 4px;
}
.input-look {
    background: #fff;
    border: 1px solid #cfcfcf;
    border-radius: 8px;
    padding: 6px 8px;
    font-size: 14px;
    color: #222;
    min-width: 170px;
    text-align: center;
    white-space: nowrap;
}

/* ===== Number colors ===== */
.num-green {
    color: var(--green);
}
.num-yellow {
    color: var(--yellow);
}
.num-red {
    color: var(--red);
}
.num-blue {
    color: var(--blue);
}
.num-dark {
    color: #222;
}

/* ===== Dotted divider ===== */
.ddiv {
    border: 0;
    border-top: 1px dotted #bdbdbd;
    margin: 18px 6px;
}

/* ===== Screen 1: Header / Avatar ===== */
.header1 {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 6px 4px 16px;
    position: relative;
}
.avatar-wrap {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: #fde9a7;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 120px;
    position: relative;
}
.avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}
.avatar-wrap svg {
    width: 90px;
    height: 90px;
}
.name-block {
    margin-left: 14px;
    margin-top: 10px;
}
.name-pill {
    background: var(--green-pill);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 16px;
    padding: 14px 38px;
    font-size: 24px;
    font-weight: 600;
    text-align: center;
}
.name-date {
    text-align: right;
    margin-top: 6px;
    font-size: 14px;
    color: #333;
    padding-right: 6px;
}

/* ===== Pill row ===== */
.pill-row {
    display: flex;
    gap: 18px;
    padding: 4px 2px 6px;
    position: relative;
}
.pill-row .pill {
    flex: 1;
    font-size: 15px;
    padding: 6px 14px;
}
.know-pill {
    position: absolute;
    right: 6px;
    top: 46px;
    background: var(--pink);
    color: #b14747;
    border-radius: 14px;
    font-size: 12px;
    padding: 6px 10px;
    line-height: 1.15;
    text-align: center;
    border: 1px solid rgba(0, 0, 0, 0.05);
    font-weight: 600;
}

.time-range {
    color: var(--blue-text);
    font-size: 14px;
    margin: 10px 0 6px;
    letter-spacing: 0.5px;
}

.stat-row {
    display: grid;
    grid-template-columns: 130px 1fr;
    align-items: center;
    column-gap: 8px;
    padding: 8px 4px;
}

/* ===== Screen 2/3/4 header ===== */
.top-header {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 4px 2px 10px;
    position: relative;
    min-height: 90px;
}
.back-pill {
    background: var(--green-pill);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 6px 14px;
    font-size: 15px;
    font-weight: 600;
    margin-top: 6px;
    position: relative;
    cursor: pointer;
}
.page-title {
    margin-left: auto;
    background: var(--beige-pill);
    border-radius: 18px;
    padding: 18px 36px;
    font-size: 28px;
    font-weight: 700;
    color: #222;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

/* ===== Section header bar ===== */
.sec-title {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #d2d2d2;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 17px;
    margin-top: 18px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
}
.sec-title .kw {
    color: #2c2c2c;
    font-weight: 700;
    border-bottom: 8px solid #bfe6ed;
    line-height: 0.9;
    padding-bottom: 1px;
    margin-right: 6px;
}
.sec-title.blue .kw {
    color: #3a6db4;
    border-bottom-color: #bfd6ef;
}

.chart-card {
    background: #efefef;
    border-radius: 6px;
    padding: 14px 10px 8px;
    margin-top: 8px;
}

/* ===== Legend ===== */
.legend {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 11px;
    color: #333;
    padding: 0 4px 8px;
}
.legend .sw {
    display: inline-block;
    width: 14px;
    height: 10px;
    border-radius: 2px;
    margin-right: 4px;
    vertical-align: middle;
}
.legend .sw-dot {
    border-radius: 50%;
    width: 8px;
    height: 8px;
}

/* ===== Table ===== */
table.data {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
    font-size: 12px;
}
table.data th {
    background: #dcdcdc;
    text-align: left;
    padding: 6px 4px;
    font-weight: 600;
    font-size: 11px;
    white-space: nowrap;
    color: #333;
    border-bottom: 1px solid #c2c2c2;
}
table.data td {
    padding: 6px 4px;
    font-size: 11px;
    border-bottom: 1px solid #d6d6d6;
    color: #222;
}
table.data tr:nth-child(even) td {
    background: #eaeaea;
}
table.data tr:nth-child(odd) td {
    background: #cfcfcf;
}
.pager {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    align-items: center;
    font-size: 12px;
    color: #555;
    padding: 6px 4px;
}
.pager .arrow {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #888;
}

/* ===== Screen 3: Hourly / Input rows ===== */
.input-row {
    display: grid;
    grid-template-columns: 90px 1fr;
    align-items: center;
    column-gap: 14px;
    padding: 8px 4px;
    font-size: 17px;
}
.input-row .input-look {
    font-size: 14px;
    min-width: 0;
    width: 100%;
    text-align: center;
}

.color-legend {
    background: #fff;
    border-radius: 6px;
    padding: 10px 12px;
    margin-top: 16px;
    font-size: 14px;
}
.clg-grid {
    display: grid;
    grid-template-columns: 80px 1fr 1fr;
    row-gap: 10px;
    column-gap: 12px;
    align-items: center;
}
.sw-clg {
    display: inline-block;
    width: 30px;
    height: 18px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    margin-right: 8px;
    vertical-align: middle;
}

/* ===== Timeline ===== */
.timeline-wrap {
    border: 1.5px solid #2c2c2c;
    border-radius: 8px;
    padding: 16px 14px;
    margin-top: 16px;
    background: #fff;
}
.timeline {
    margin: 14px 0;
}
.timeline:first-child {
    margin-top: 0;
}
.timeline:last-child {
    margin-bottom: 0;
}
.tl-bar {
    height: 22px;
    border-radius: 1px;
    background: #fff;
    position: relative;
}
.tl-bar.day {
    background: linear-gradient(90deg, #e6a836, #f0bf57 50%, #e6a836);
}
.tl-bar.night {
    background: linear-gradient(90deg, #7d83cc, #3f3f9c 50%, #7d83cc);
}
.tl-icon {
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 34px;
    height: 34px;
    border-radius: 6px;
    background: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
}
.tl-data {
    height: 24px;
    background: #cfcfcf;
    display: flex;
    margin-top: 0;
    position: relative;
}
.tl-axis {
    display: flex;
    justify-content: space-between;
    padding: 4px 0 0;
    font-size: 11px;
    color: #444;
    position: relative;
}

/* ===== Screen 4: Breathing detail ===== */
.br-range {
    display: flex;
    gap: 14px;
    align-items: baseline;
    padding: 8px 4px;
    font-size: 18px;
}
.br-range .t {
    color: var(--blue-text);
    font-size: 14px;
    margin-left: 4px;
}
.br-stat-grid {
    display: grid;
    grid-template-columns: 130px 1fr;
    row-gap: 6px;
    align-items: center;
    padding: 6px 4px;
}
.br-stat-grid .v {
    font-size: 18px;
    font-weight: 600;
}
.br-mini-legend {
    display: flex;
    gap: 18px;
    justify-content: center;
    font-size: 14px;
    padding: 8px;
}
.br-mini-legend .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--dot);
    display: inline-block;
    margin-right: 6px;
    vertical-align: middle;
}
.br-mini-legend .ln {
    width: 18px;
    height: 2px;
    background: var(--trend);
    display: inline-block;
    margin-right: 6px;
    vertical-align: middle;
}

/* ===== Scatter chart ===== */
.scatter-wrap {
    border: 1.5px solid #2c2c2c;
    border-radius: 8px;
    padding: 0;
    margin-top: 8px;
    background: #fff;
    overflow: hidden;
}
.scatter-block {
    padding: 0 0 4px;
}
.scatter-block + .scatter-block {
    border-top: 1px solid #e0e0e0;
}

/* ===== Date Picker ===== */
.hcr-picker {
    padding: 14px 4px 0;
}
.hcr-picker >>> .field-input {
    background: #e9e9e9 !important;
    border: 1px solid #cfcfcf !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    text-align: center;
    height: 40px !important;
    min-height: 40px !important;
}

/* ===== Center ===== */
.hcr-center {
    text-align: center;
    padding: 60px 20px;
    color: #999;
}

/* ===== Sleep Tips ===== */
.tips-section { padding: 0 12px 24px; }
.tips-banner {
    display: block;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    padding: 10px;
    margin: 12px 0;
    border-radius: 8px;
    text-decoration: none;
}
.tips-banner.pink { background: #f8bbd0; color: #880e4f; }
.tips-banner.blue { background: #bbdefb; color: #0d47a1; }

.breathing-card {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
}
.breathing-card p {
    font-size: 14px;
    line-height: 1.7;
    margin: 8px 0;
    color: #333;
}
.breathing-card h4 {
    font-size: 15px;
    color: #1976d2;
    margin: 16px 0 8px;
}
.breathing-card ol {
    padding-left: 20px;
    margin: 8px 0;
    font-size: 14px;
    line-height: 1.8;
    color: #333;
}
.breathing-section-title {
    font-size: 18px;
    font-weight: bold;
    text-align: center;
    padding: 8px;
    border-radius: 8px;
    margin-bottom: 12px;
}
.breathing-section-title.green { background: #c8e6c9; color: #1b5e20; }
.breathing-section-title.orange { background: #ffe0b2; color: #e65100; }
.breathing-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.breathing-table td {
    border: 1px solid #ddd;
    padding: 10px 14px;
    line-height: 1.6;
}
.breathing-table .label-cell {
    width: 60px;
    text-align: center;
    font-weight: bold;
    background: #f5f5f5;
}
.breathing-list {
    padding-left: 24px;
}
.breathing-list li {
    margin-bottom: 12px;
}

.tips-poster-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
}
.tips-poster {
    flex: 0 0 55%;
    cursor: pointer;
}
.tips-poster img {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}
.tips-links {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.tips-links-title {
    font-weight: bold;
    font-size: 14px;
    color: #555;
    margin-bottom: 4px;
}
.tips-thumb {
    cursor: pointer;
    padding: 8px 12px;
    background: #f5f5f5;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
}
.tips-thumb span {
    font-size: 13px;
    color: #1976d2;
}
.tips-chart-section { margin-bottom: 16px; }
.tips-subtitle {
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    color: #e65100;
    margin: 12px 0 8px;
}
.tips-full-img {
    width: 100%;
    border-radius: 8px;
    cursor: pointer;
}
.tips-rules-section { margin-bottom: 16px; }
.tips-rules-cols {
    display: flex;
    gap: 8px;
    margin-top: 8px;
}
.tips-rules-cols img {
    flex: 1;
    width: 0;
    border-radius: 8px;
    cursor: pointer;
}

/* Modal */
.tip-modal-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.85);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.tip-modal-content {
    position: relative;
    max-width: 95vw;
    max-height: 90vh;
}
.tip-modal-close {
    position: absolute;
    top: -12px; right: -12px;
    width: 32px; height: 32px;
    border-radius: 50%;
    background: #fff;
    border: none;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    z-index: 1;
}
.tip-modal-img {
    max-width: 95vw;
    max-height: 85vh;
    object-fit: contain;
    border-radius: 4px;
}

@media (max-width: 480px) {
    .tips-poster-row { flex-direction: column; }
    .tips-poster { flex: none; }
    .tips-rules-cols { flex-direction: column; }
    .tips-rules-cols img { width: 100%; }
}
</style>
